// Scoring engine for all 5 game types
// All functions are pure — they take data in and return results.

/**
 * Main dispatcher: compute round results for any game type.
 * @param {string} gameType - Game type ID (top2, duels, hero, four_ball, next_ball)
 * @param {Array} teams - [{ name, playerIds: [uuid] }]
 * @param {Array} scores - [{ player_id, hole_number, strokes }]
 * @param {Array} handicapBreakdown - [{ playerId, strokeHoles: Map<hole, strokes> }] from buildHandicapBreakdown
 * @param {number} numHoles - 9 or 18
 * @returns {Object} { holeResults, standings, nassau?, aggregate? }
 */
/**
 * Main dispatcher: compute round results for any game type.
 * @param {string} gameType - Game type ID (top2, duels, hero, four_ball, next_ball)
 * @param {Array} teams - [{ name, playerIds: [uuid] }]
 * @param {Array} scores - [{ player_id, hole_number, strokes }]
 * @param {Array} handicapBreakdown - [{ playerId, strokeHoles: Map<hole, strokes> }] from buildHandicapBreakdown
 * @param {number} numHoles - 9 or 18
 * @param {Object} gameConfig - Optional game configuration (e.g. hero rotation)
 * @returns {Object} { holeResults, standings, nassau?, aggregate? }
 */
export function computeRoundResults(gameType, teams, scores, handicapBreakdown, numHoles = 18, gameConfig = {}) {
  const getNet = (playerId, hole) => {
    const gross = getGross(scores, playerId, hole)
    if (gross == null) return null
    const hcp = handicapBreakdown?.find((h) => h.playerId === playerId)
    const strokesOnHole = hcp?.strokeHoles?.get(hole) || 0
    return gross - strokesOnHole
  }

  let result
  switch (gameType) {
    case 'solo_match_play':
      result = scoreSoloMatchPlay(teams, scores, getNet, numHoles)
      break
    case 'net_stroke_play':
      result = scoreNetStrokePlay(teams, scores, getNet, numHoles)
      break
    case 'top2':
      result = scoreTop2(teams, scores, getNet, numHoles)
      break
    case 'duels':
      result = scoreDuels(teams, scores, getNet, numHoles)
      break
    case 'batman':
    case 'hero':
      result = scoreHero(teams, scores, getNet, numHoles, gameConfig)
      break
    case 'high_low':
      result = scoreHighLow(teams, scores, getNet, numHoles)
      break
    case 'four_ball':
      result = scoreFourBall(teams, scores, getNet, numHoles)
      break
    case 'next_ball':
      result = scoreNextBall(teams, scores, getNet, numHoles)
      break
    case 'stableford':
      result = scoreStableford(teams, scores, getNet, numHoles, gameConfig)
      break
    case 'six_pack':
      result = scoreSixPack(teams, scores, getNet, numHoles)
      break
    case 'scramble':
      result = scoreScramble(teams, scores, getNet, numHoles)
      break
    default:
      result = scoreStroke(teams, scores, getNet, numHoles)
      break
  }

  return applyNassauWeights(applyStandardFormat(result, gameConfig), gameConfig)
}

// ============================================
// Helpers
// ============================================

function getGross(scores, playerId, hole) {
  const s = scores.find((sc) => sc.player_id === playerId && sc.hole_number === hole)
  return s?.strokes ?? null
}

function holeHasAllScores(teams, scores, hole) {
  return teams.every((team) =>
    team.playerIds.every((pid) =>
      scores.some((s) => s.player_id === pid && s.hole_number === hole && s.strokes != null)
    )
  )
}

/** Compute Nassau points from hole results: front 9, back 9, overall 18 */
function computeNassau(holeResults, numTeams, options = {}) {
  const pointsPerHole = options.pointsPerHole || 1

  const tally = (startHole, endHole) => {
    const teamWins = new Array(numTeams).fill(0)
    for (const hr of holeResults) {
      if (hr.hole >= startHole && hr.hole <= endHole && !hr.pending) {
        if (hr.teamPoints) {
          for (let i = 0; i < numTeams; i++) teamWins[i] += (hr.teamPoints[i] || 0)
        } else if (hr.winner != null) {
          teamWins[hr.winner]++
        }
      }
    }
    return teamWins
  }

  // Count how many holes have been played (have scores) in a range
  const holesPlayed = (startHole, endHole) => {
    return holeResults.filter(
      (hr) => hr.hole >= startHole && hr.hole <= endHole && !hr.pending
    ).length
  }

  const frontWins = tally(1, 9)
  const backWins = tally(10, 18)
  const overallWins = tally(1, 18)

  const pointWinner = (wins) => {
    const max = Math.max(...wins)
    const winners = wins.filter((w) => w === max)
    if (winners.length > 1) return null // tie
    return wins.indexOf(max)
  }

  // Compute dormie state for a segment
  const dormieState = (wins, totalHoles, played) => {
    if (numTeams !== 2) return null
    const remaining = totalHoles - played
    if (remaining <= 0) return null
    const diff = wins[0] - wins[1]
    if (diff === 0) return null // tied, no dormie
    // The losing team is dormie when deficit >= remaining possible points
    const losingTeam = diff > 0 ? 1 : 0
    const deficit = Math.abs(diff)
    if (deficit >= remaining * pointsPerHole) {
      return { losingTeam, deficit, remaining, isDormie: true }
    }
    return null
  }

  const frontPlayed = holesPlayed(1, 9)
  const backPlayed = holesPlayed(10, 18)
  const overallPlayed = holesPlayed(1, 18)

  return {
    front: {
      wins: frontWins,
      winner: pointWinner(frontWins),
      dormie: dormieState(frontWins, 9, frontPlayed),
      complete: frontPlayed === 9,
    },
    back: {
      wins: backWins,
      winner: pointWinner(backWins),
      dormie: dormieState(backWins, 9, backPlayed),
      complete: backPlayed === 9,
    },
    overall: {
      wins: overallWins,
      winner: pointWinner(overallWins),
      dormie: dormieState(overallWins, 18, overallPlayed),
      complete: overallPlayed === 18,
    },
    points: Array.from({ length: numTeams }, (_, i) => {
      let pts = 0
      const fw = pointWinner(frontWins)
      const bw = pointWinner(backWins)
      const ow = pointWinner(overallWins)
      if (fw === i) pts++
      else if (fw === null && frontPlayed > 0) pts += 0.5
      if (bw === i) pts++
      else if (bw === null && backPlayed > 0) pts += 0.5
      if (ow === i) pts++
      else if (ow === null && overallPlayed > 0) pts += 0.5
      return pts
    }),
  }
}

/**
 * When matchFormat is 'standard', null out front/back segment winners
 * so only the overall segment pays out. Data structure stays identical.
 */
function applyStandardFormat(result, gameConfig) {
  if (!gameConfig || gameConfig.matchFormat !== 'standard') return result

  if (result.format === 'nassau' && result.nassau) {
    result.nassau.front.winner = null
    result.nassau.back.winner = null
    // Recalculate points: only overall counts
    const numTeams = result.nassau.points.length
    result.nassau.points = Array.from({ length: numTeams }, (_, i) => {
      if (result.nassau.overall.winner === i) return 1
      if (result.nassau.overall.winner === null && result.nassau.overall.complete) return 0.5
      return 0
    })
    // Update standings to reflect new points
    if (result.standings) {
      result.standings = result.standings.map((s, i) => ({
        ...s,
        points: result.nassau.points[i],
      }))
    }
  }

  if (result.format === 'stableford' && result.stableford) {
    result.stableford.front.winner = null
    result.stableford.back.winner = null
    // Update standings points
    if (result.standings) {
      result.standings = result.standings.map((s, i) => ({
        ...s,
        points: result.stableford.overall.winner === i ? 1 : (result.stableford.overall.winner === null && result.stableford.overall.complete ? 0.5 : 0),
      }))
    }
  }

  if (result.format === 'six_pack' && result.six_pack) {
    result.six_pack.front.winner = null
    result.six_pack.back.winner = null
    // Update standings points
    if (result.standings) {
      result.standings = result.standings.map((s, i) => ({
        ...s,
        points: result.six_pack.overall.winner === i ? 1 : (result.six_pack.overall.winner === null && result.six_pack.overall.complete ? 0.5 : 0),
      }))
    }
  }

  return result
}

/**
 * Apply nassau segment weights (e.g. 1-1-2 where overall is worth double).
 * Must run AFTER applyStandardFormat.
 */
function applyNassauWeights(result, gameConfig) {
  if (!gameConfig?.nassauWeights) return result
  if (gameConfig.matchFormat === 'standard') return result
  const [fw, bw, ow] = gameConfig.nassauWeights
  if (fw === 1 && bw === 1 && ow === 1) return result

  const applyWeights = (segmented) => {
    if (!segmented) return
    const numTeams = segmented.points.length
    segmented.points = Array.from({ length: numTeams }, (_, i) => {
      let pts = 0
      const { front, back, overall } = segmented
      if (front.winner === i) pts += fw
      else if (front.winner === null && front.complete) pts += fw * 0.5
      if (back.winner === i) pts += bw
      else if (back.winner === null && back.complete) pts += bw * 0.5
      if (overall.winner === i) pts += ow
      else if (overall.winner === null && overall.complete) pts += ow * 0.5
      return pts
    })
  }

  if (result.format === 'nassau' && result.nassau) {
    applyWeights(result.nassau)
  } else if (result.format === 'stableford' && result.stableford) {
    applyWeights(result.stableford)
  } else if (result.format === 'six_pack' && result.six_pack) {
    applyWeights(result.six_pack)
  }

  if (result.standings) {
    const segmented = result.nassau || result.stableford || result.six_pack
    if (segmented) {
      result.standings = result.standings.map((s, i) => ({
        ...s,
        points: segmented.points[i],
      }))
    }
  }

  return result
}

/**
 * Detect dormie status across all segments of a match play round.
 * Returns an array of segments that are currently dormie.
 * Backward-compat wrapper — for old rounds with no pressConfig.
 * @param {Object} roundResults - Output from computeRoundResults
 * @returns {Array} [{ segment: 'front'|'back'|'overall', losingTeam, deficit, remaining }]
 */
export function getDormieSegments(roundResults) {
  if (!roundResults) return []

  const dormies = []

  if (roundResults.format === 'nassau' && roundResults.nassau) {
    for (const seg of ['front', 'back', 'overall']) {
      const d = roundResults.nassau[seg]?.dormie
      if (d?.isDormie) {
        dormies.push({ segment: seg, ...d })
      }
    }
  }

  if (roundResults.format === 'per_nine' && roundResults.nineResults) {
    roundResults.nineResults.forEach((nr, idx) => {
      if (nr.teamWins && nr.winner == null) {
        const label = idx === 0 ? 'front' : 'back'
        const totalHoles = 9
        const played = nr.teamWins[0] + nr.teamWins[1]
        const remaining = totalHoles - played
        if (remaining > 0) {
          const diff = nr.teamWins[0] - nr.teamWins[1]
          if (diff !== 0) {
            const losingTeam = diff > 0 ? 1 : 0
            const deficit = Math.abs(diff)
            if (deficit >= remaining) {
              dormies.push({ segment: label, losingTeam, deficit, remaining, isDormie: true })
            }
          }
        }
      }
    })
  }

  return dormies
}

/**
 * Compute press triggers for the full configurable press system.
 * Returns an array of trigger objects describing which segments can be pressed and by whom.
 *
 * @param {Object} roundResults - Output from computeRoundResults
 * @param {Object} pressConfig - Press configuration from game_config.pressConfig
 * @param {Array} existingPresses - Current presses [{ metadata: { segment, pressing_team, parent_press_id }, hole_start, id }]
 * @param {number} currentHole - Current hole being played
 * @param {number} numHoles - Total holes (9 or 18)
 * @returns {Array} triggers [{ segment, losingTeam, deficit, remaining, canPress, autoTrigger, isDormie, pressStartHole, parentPressId? }]
 */
export function getPressTriggers(roundResults, pressConfig, existingPresses = [], currentHole = 1, numHoles = 18, gameConfig = {}) {
  if (!roundResults || !pressConfig) return []
  const isStandard = gameConfig?.matchFormat === 'standard'

  // Map legacy trigger mode IDs to new simplified IDs
  const normalizeTrigger = (mode) => {
    if (!mode) return mode
    if (mode === 'manual_1_down' || mode === 'auto_1_down') return '1_down'
    if (mode === 'manual_2_down' || mode === 'auto_2_down') return '2_down'
    if (mode === 'manual_points' || mode === 'auto_points') return '1_down'
    return mode
  }

  const triggers = []
  const presses = existingPresses || []

  // Determine thresholds from config
  const isMatchPlay = roundResults.type === 'match_play'
  const triggerMode = normalizeTrigger(isMatchPlay ? pressConfig.triggerMode : pressConfig.strokePlayTrigger)
  if (!triggerMode) return []

  const threshold = triggerMode === '1_down' ? 1 : triggerMode === '2_down' ? 2 : 0
  const pressLimit = pressConfig.pressLimit === 'unlimited' ? Infinity : (Number(pressConfig.pressLimit) || Infinity)

  // Helper: count existing presses for a segment (including re-presses)
  const countPresses = (segment, parentId) => {
    return presses.filter(p => {
      if (parentId) return p.metadata?.parent_press_id === parentId
      return p.metadata?.segment === segment
    }).length
  }

  // Helper: find the next unscored hole from a start position
  const nextUnscoredHole = (start, end) => {
    if (!roundResults.holeResults) return start
    for (let h = start; h <= end; h++) {
      const hr = roundResults.holeResults.find(r => r.hole === h)
      if (!hr || hr.pending) return h
    }
    return end + 1 // all scored
  }

  // Define segments to check based on format
  const segments = []
  if (roundResults.format === 'nassau' && roundResults.nassau) {
    if (!isStandard) segments.push({ key: 'front', start: 1, end: 9 })
    if (numHoles > 9) {
      if (!isStandard) segments.push({ key: 'back', start: 10, end: 18 })
      segments.push({ key: 'overall', start: 1, end: 18 })
    }
  } else if (roundResults.format === 'per_nine' && roundResults.nineResults) {
    segments.push({ key: 'front', start: 1, end: 9 })
    if (numHoles > 9) segments.push({ key: 'back', start: 10, end: 18 })
  } else if (roundResults.format === 'stableford' && roundResults.stableford) {
    if (!isStandard) segments.push({ key: 'front', start: 1, end: 9 })
    if (numHoles > 9) {
      if (!isStandard) segments.push({ key: 'back', start: 10, end: 18 })
      segments.push({ key: 'overall', start: 1, end: 18 })
    }
  } else if (roundResults.format === 'six_pack' && roundResults.six_pack) {
    if (!isStandard) segments.push({ key: 'front', start: 1, end: 9 })
    if (numHoles > 9) {
      if (!isStandard) segments.push({ key: 'back', start: 10, end: 18 })
      segments.push({ key: 'overall', start: 1, end: 18 })
    }
  }

  for (const seg of segments) {
    // Block Total press until back 9 — pressing Total while still on the front is redundant
    if (seg.key === 'overall' && currentHole <= 9) continue

    // Check press limit
    if (countPresses(seg.key) >= pressLimit) continue

    // Check if segment is already complete
    const holesPlayed = roundResults.holeResults
      ? roundResults.holeResults.filter(hr => hr.hole >= seg.start && hr.hole <= seg.end && !hr.pending).length
      : 0
    const totalHoles = seg.end - seg.start + 1
    const remaining = totalHoles - holesPlayed
    if (remaining <= 0) continue

    let deficit = 0
    let losingTeam = null

    if (isMatchPlay) {
      // Match play: count hole wins per team
      let wins
      if (roundResults.format === 'nassau') {
        wins = roundResults.nassau[seg.key]?.wins
      } else if (roundResults.format === 'per_nine') {
        const nrIdx = seg.key === 'front' ? 0 : 1
        wins = roundResults.nineResults?.[nrIdx]?.teamWins
      }
      if (!wins || wins.length < 2) continue
      const diff = wins[0] - wins[1]
      if (diff === 0) continue // tied, no trigger
      losingTeam = diff > 0 ? 1 : 0
      deficit = Math.abs(diff)
    } else {
      // Stroke play: compute point/score deficit
      let teamTotals
      if (roundResults.format === 'stableford') {
        teamTotals = roundResults.stableford?.[seg.key]?.totals
        if (!teamTotals || teamTotals.length < 2) continue
        // Stableford: higher is better
        const diff = teamTotals[0] - teamTotals[1]
        if (diff === 0) continue
        losingTeam = diff > 0 ? 1 : 0 // team with fewer points is losing
        deficit = Math.abs(diff)
      } else if (roundResults.format === 'six_pack') {
        const segData = roundResults.six_pack?.[seg.key]?.data
        if (!segData || segData.length < 2) continue
        // Six Pack: lower is better
        const diff = segData[0].total - segData[1].total
        if (diff === 0) continue
        losingTeam = diff > 0 ? 0 : 1 // team with higher total is losing (higher = worse)
        deficit = Math.abs(diff)
      } else if (roundResults.format === 'nassau' && roundResults.type === 'stroke_play') {
        // Duels: lower is better
        teamTotals = roundResults.nassau?.[seg.key]?.totals
        if (!teamTotals || teamTotals.length < 2) continue
        const diff = teamTotals[0] - teamTotals[1]
        if (diff === 0) continue
        losingTeam = diff > 0 ? 0 : 1 // team with higher total is losing
        deficit = Math.abs(diff)
      }
    }

    // For 'none' threshold (0), allow triggers even when tied — either team can press
    if (triggerMode === 'none') {
      if (losingTeam == null) {
        deficit = 0
      }
    } else {
      if (losingTeam == null || deficit <= 0) continue
    }

    const hasTeamPts = roundResults.holeResults?.some(hr => hr.teamPoints)
    const ppHole = hasTeamPts ? 2 : 1
    const isDormie = isMatchPlay && deficit >= remaining * ppHole
    const meetsThreshold = deficit >= threshold

    // Determine press start hole
    const pressStartHole = nextUnscoredHole(seg.start, seg.end)

    if (meetsThreshold) {
      triggers.push({
        segment: seg.key,
        losingTeam: triggerMode === 'none' ? null : losingTeam,
        deficit,
        remaining,
        canPress: true,
        autoTrigger: false,
        isDormie,
        pressStartHole,
      })
    }
  }

  // Press-on-press detection
  if (pressConfig.pressOnPress && presses.length > 0) {
    for (const press of presses) {
      const seg = press.metadata?.segment
      const pressStart = press.hole_start
      const segEnd = seg === 'front' ? 9 : seg === 'back' ? 18 : numHoles

      // Check re-press limit
      const rePressCount = presses.filter(p => p.metadata?.parent_press_id === press.id).length
      if (rePressCount >= pressLimit) continue

      // Compute wins within press range
      const pressHoles = roundResults.holeResults
        ? roundResults.holeResults.filter(hr => hr.hole >= pressStart && hr.hole <= segEnd && !hr.pending)
        : []
      const pressRemaining = (segEnd - pressStart + 1) - pressHoles.length
      if (pressRemaining <= 0) continue

      if (isMatchPlay) {
        const wins = [0, 0]
        for (const hr of pressHoles) {
          if (hr.teamPoints) {
            wins[0] += (hr.teamPoints[0] || 0)
            wins[1] += (hr.teamPoints[1] || 0)
          } else if (hr.winner != null) {
            wins[hr.winner]++
          }
          if (hr.matchResults) {
            for (const mr of hr.matchResults) { if (mr.winner != null) wins[mr.winner]++ }
          }
        }
        const diff = wins[0] - wins[1]
        if (diff === 0) continue
        const losingTeam = diff > 0 ? 1 : 0
        const deficit = Math.abs(diff)
        if (deficit >= threshold) {
          // Don't duplicate a trigger that already exists
          const alreadyTriggered = triggers.some(t => t.segment === seg && t.parentPressId === press.id)
          if (!alreadyTriggered) {
            triggers.push({
              segment: seg,
              losingTeam,
              deficit,
              remaining: pressRemaining,
              canPress: true,
              autoTrigger: false,
              isDormie: deficit >= pressRemaining * (roundResults.holeResults?.some(hr => hr.teamPoints) ? 2 : 1),
              pressStartHole: nextUnscoredHole(pressStart, segEnd),
              parentPressId: press.id,
            })
          }
        }
      }
      // Stroke play re-press: use same deficit logic but within press range
      // (simplified — uses overall segment deficit which is close enough for this use case)
    }
  }

  // Late hole press — uses per-hole selection or falls back to [16] for legacy configs
  if (pressConfig.lastHolePress && numHoles === 18) {
    const lateHoleHoles = pressConfig.lastHoleHoles || [16]
    if (lateHoleHoles.includes(currentHole)) {
      for (const seg of segments) {
        // Only trigger if no existing trigger for this segment and no press already exists for this hole
        const hasExistingTrigger = triggers.some(t => t.segment === seg.key && !t.parentPressId)
        const hasExistingPress = presses.some(p => p.metadata?.segment === seg.key && p.hole_start >= Math.min(...lateHoleHoles))
        if (!hasExistingTrigger && !hasExistingPress) {
          const holesPlayed = roundResults.holeResults
            ? roundResults.holeResults.filter(hr => hr.hole >= seg.start && hr.hole <= seg.end && !hr.pending).length
            : 0
          const remaining = (seg.end - seg.start + 1) - holesPlayed
          if (remaining > 0) {
            triggers.push({
              segment: seg.key,
              losingTeam: null, // either team can press
              deficit: 0,
              remaining,
              canPress: true,
              autoTrigger: false,
              isDormie: false,
              pressStartHole: currentHole,
              isLastHolePress: true,
            })
          }
        }
      }
    }
  }

  return triggers
}

// ============================================
// Solo Match Play (1v1) — individual match play, Nassau
// ============================================
function scoreSoloMatchPlay(teams, scores, getNet, numHoles) {
  const holeResults = []

  for (let hole = 1; hole <= numHoles; hole++) {
    if (!holeHasAllScores(teams, scores, hole)) {
      holeResults.push({ hole, winner: null, teamScores: [], pending: true })
      continue
    }

    const teamScores = teams.map((team, idx) => {
      const net = getNet(team.playerIds[0], hole)
      return { teamIndex: idx, net }
    })

    const validScores = teamScores.filter((t) => t.net != null)
    const minNet = Math.min(...validScores.map((t) => t.net))
    const winners = validScores.filter((t) => t.net === minNet)
    const winner = winners.length === 1 ? winners[0].teamIndex : null // tie = pushed

    holeResults.push({ hole, winner, teamScores, pending: false })
  }

  const nassau = computeNassau(holeResults, teams.length)

  return {
    type: 'match_play',
    format: 'nassau',
    holeResults,
    nassau,
    standings: nassau.points.map((pts, i) => ({ teamIndex: i, teamName: teams[i]?.name, points: pts })),
  }
}

// ============================================
// Net Stroke Play (individual) — aggregate net stroke play, Nassau
// ============================================
function scoreNetStrokePlay(teams, scores, getNet, numHoles) {
  const holeResults = []

  for (let hole = 1; hole <= numHoles; hole++) {
    const teamScores = teams.map((team, idx) => {
      const net = getNet(team.playerIds[0], hole)
      // Use bestBall for compatibility with press code paths (1-player team = bestBall is the player's net)
      return { teamIndex: idx, bestBall: net }
    })

    const pending = !holeHasAllScores(teams, scores, hole)
    let winner = null
    if (!pending) {
      const validScores = teamScores.filter((t) => t.bestBall != null)
      const minNet = Math.min(...validScores.map((t) => t.bestBall))
      const winners = validScores.filter((t) => t.bestBall === minNet)
      if (winners.length === 1) winner = winners[0].teamIndex
    }

    holeResults.push({ hole, winner, teamScores, pending })
  }

  // Compute aggregate team totals per segment
  const segmentTotal = (startHole, endHole) => {
    return teams.map((_, idx) => {
      let total = 0
      for (const hr of holeResults) {
        if (hr.hole >= startHole && hr.hole <= endHole && !hr.pending) {
          const ts = hr.teamScores.find((t) => t.teamIndex === idx)
          if (ts?.bestBall != null) total += ts.bestBall
        }
      }
      return total
    })
  }

  const holesPlayed = (startHole, endHole) =>
    holeResults.filter((hr) => hr.hole >= startHole && hr.hole <= endHole && !hr.pending).length

  const segmentWinner = (totals) => {
    const nonZero = totals.filter((t) => t > 0)
    if (nonZero.length === 0) return null // no scores yet
    const min = Math.min(...nonZero)
    const winners = totals.filter((t) => t === min && t > 0)
    if (winners.length > 1) return null // tie
    return totals.indexOf(min)
  }

  const frontTotals = segmentTotal(1, 9)
  const backTotals = segmentTotal(10, 18)
  const overallTotals = segmentTotal(1, 18)

  // Also compute hole-win tracking for display
  const matchNassau = computeNassau(holeResults, teams.length)

  const nassau = {
    front: {
      ...matchNassau.front,
      totals: frontTotals,
      winner: segmentWinner(frontTotals),
    },
    back: {
      ...matchNassau.back,
      totals: backTotals,
      winner: segmentWinner(backTotals),
    },
    overall: {
      ...matchNassau.overall,
      totals: overallTotals,
      winner: segmentWinner(overallTotals),
    },
    points: teams.map((_, i) => {
      let pts = 0
      if (segmentWinner(frontTotals) === i) pts++
      if (segmentWinner(backTotals) === i) pts++
      if (segmentWinner(overallTotals) === i) pts++
      return pts
    }),
  }

  const teamTotals = overallTotals

  return {
    type: 'stroke_play',
    format: 'nassau',
    holeResults,
    nassau,
    teamTotals,
    standings: nassau.points.map((pts, i) => ({ teamIndex: i, teamName: teams[i]?.name, points: pts, aggregate: teamTotals[i] })),
  }
}

// ============================================
// Top 2 (3v3 or 4v4)
// ============================================
function scoreTop2(teams, scores, getNet, numHoles) {
  const holeResults = []

  for (let hole = 1; hole <= numHoles; hole++) {
    if (!holeHasAllScores(teams, scores, hole)) {
      holeResults.push({ hole, winner: null, teamScores: [], pending: true })
      continue
    }

    const teamScores = teams.map((team, idx) => {
      const nets = team.playerIds
        .map((pid) => getNet(pid, hole))
        .filter((n) => n != null)
        .sort((a, b) => a - b)
      // Sum top 2 lowest
      const top2Sum = nets.slice(0, 2).reduce((s, n) => s + n, 0)
      return { teamIndex: idx, top2Sum, nets }
    })

    const minSum = Math.min(...teamScores.map((t) => t.top2Sum))
    const winners = teamScores.filter((t) => t.top2Sum === minSum)
    const winner = winners.length === 1 ? winners[0].teamIndex : null // pushed if tie

    holeResults.push({ hole, winner, teamScores, pending: false })
  }

  const nassau = computeNassau(holeResults, teams.length)

  // Compute aggregate team totals (sum of top-2 net scores across all scored holes)
  const teamTotals = teams.map((team, idx) => {
    let total = 0
    for (const hr of holeResults) {
      if (!hr.pending) {
        const ts = hr.teamScores.find(t => t.teamIndex === idx)
        if (ts) total += ts.top2Sum
      }
    }
    return total
  })

  return {
    type: 'match_play',
    format: 'nassau',
    holeResults,
    nassau,
    teamTotals,
    standings: nassau.points.map((pts, i) => ({ teamIndex: i, teamName: teams[i]?.name, points: pts, aggregate: teamTotals[i] })),
  }
}

// ============================================
// Duels (2v2v2 or 2v2v2v2) — best ball stroke play, Nassau format
// ============================================
function scoreDuels(teams, scores, getNet, numHoles) {
  const holeResults = []

  for (let hole = 1; hole <= numHoles; hole++) {
    const teamScores = teams.map((team, idx) => {
      const nets = team.playerIds
        .map((pid) => getNet(pid, hole))
        .filter((n) => n != null)
      const bestBall = nets.length > 0 ? Math.min(...nets) : null
      return { teamIndex: idx, bestBall, nets }
    })

    const pending = !holeHasAllScores(teams, scores, hole)
    // Determine hole winner (lowest best ball) for match play tracking
    let winner = null
    if (!pending) {
      const minBest = Math.min(...teamScores.map((t) => t.bestBall))
      const winners = teamScores.filter((t) => t.bestBall === minBest)
      if (winners.length === 1) winner = winners[0].teamIndex
    }

    holeResults.push({ hole, winner, teamScores, pending })
  }

  // Compute aggregate team totals per segment
  const segmentTotal = (startHole, endHole) => {
    return teams.map((_, idx) => {
      let total = 0
      for (const hr of holeResults) {
        if (hr.hole >= startHole && hr.hole <= endHole && !hr.pending) {
          const ts = hr.teamScores.find((t) => t.teamIndex === idx)
          if (ts?.bestBall != null) total += ts.bestBall
        }
      }
      return total
    })
  }

  const holesPlayed = (startHole, endHole) =>
    holeResults.filter((hr) => hr.hole >= startHole && hr.hole <= endHole && !hr.pending).length

  const segmentWinner = (totals) => {
    const min = Math.min(...totals)
    if (totals.every((t) => t === 0)) return null // no scores yet
    const winners = totals.filter((t) => t === min)
    if (winners.length > 1) return null // tie
    return totals.indexOf(min)
  }

  const frontTotals = segmentTotal(1, 9)
  const backTotals = segmentTotal(10, 18)
  const overallTotals = segmentTotal(1, 18)

  // Also compute hole-win tracking for display (NassauBanner, computeRoundStatus)
  const matchNassau = computeNassau(holeResults, teams.length)

  const nassau = {
    front: {
      ...matchNassau.front, // wins, dormie, complete
      totals: frontTotals,
      winner: segmentWinner(frontTotals), // override: aggregate-based winner for money
    },
    back: {
      ...matchNassau.back,
      totals: backTotals,
      winner: segmentWinner(backTotals),
    },
    overall: {
      ...matchNassau.overall,
      totals: overallTotals,
      winner: segmentWinner(overallTotals),
    },
    // Points: 1 for each segment won (front, back, overall) — based on aggregate
    points: teams.map((_, i) => {
      let pts = 0
      if (segmentWinner(frontTotals) === i) pts++
      if (segmentWinner(backTotals) === i) pts++
      if (segmentWinner(overallTotals) === i) pts++
      return pts
    }),
  }

  const teamTotals = overallTotals

  return {
    type: 'stroke_play',
    format: 'nassau',
    holeResults,
    nassau,
    teamTotals,
    standings: nassau.points.map((pts, i) => ({ teamIndex: i, teamName: teams[i]?.name, points: pts, aggregate: teamTotals[i] })),
  }
}

// ============================================
// 2v2 Scramble — stroke play, one team score per hole
// Same as Net Stroke Play but uses first player's score as the team score
// (all players on a team have identical scores written via bulk upsert)
// ============================================
function scoreScramble(teams, scores, getNet, numHoles) {
  const holeResults = []

  for (let hole = 1; hole <= numHoles; hole++) {
    // Use first player on each team as representative (all team members have same score)
    const teamScores = teams.map((team, idx) => {
      const net = getNet(team.playerIds[0], hole)
      return { teamIndex: idx, bestBall: net }
    })

    const pending = !holeHasAllScores(teams, scores, hole)
    let winner = null
    if (!pending) {
      const validScores = teamScores.filter((t) => t.bestBall != null)
      const minNet = Math.min(...validScores.map((t) => t.bestBall))
      const winners = validScores.filter((t) => t.bestBall === minNet)
      if (winners.length === 1) winner = winners[0].teamIndex
    }

    holeResults.push({ hole, winner, teamScores, pending })
  }

  // Compute aggregate team totals per segment
  const segmentTotal = (startHole, endHole) => {
    return teams.map((_, idx) => {
      let total = 0
      for (const hr of holeResults) {
        if (hr.hole >= startHole && hr.hole <= endHole && !hr.pending) {
          const ts = hr.teamScores.find((t) => t.teamIndex === idx)
          if (ts?.bestBall != null) total += ts.bestBall
        }
      }
      return total
    })
  }

  const segmentWinner = (totals) => {
    const nonZero = totals.filter((t) => t > 0)
    if (nonZero.length === 0) return null
    const min = Math.min(...nonZero)
    const winners = totals.filter((t) => t === min && t > 0)
    if (winners.length > 1) return null
    return totals.indexOf(min)
  }

  const frontTotals = segmentTotal(1, 9)
  const backTotals = segmentTotal(10, 18)
  const overallTotals = segmentTotal(1, 18)

  const matchNassau = computeNassau(holeResults, teams.length)

  const nassau = {
    front: {
      ...matchNassau.front,
      totals: frontTotals,
      winner: segmentWinner(frontTotals),
    },
    back: {
      ...matchNassau.back,
      totals: backTotals,
      winner: segmentWinner(backTotals),
    },
    overall: {
      ...matchNassau.overall,
      totals: overallTotals,
      winner: segmentWinner(overallTotals),
    },
    points: teams.map((_, i) => {
      let pts = 0
      if (segmentWinner(frontTotals) === i) pts++
      if (segmentWinner(backTotals) === i) pts++
      if (segmentWinner(overallTotals) === i) pts++
      return pts
    }),
  }

  const teamTotals = overallTotals

  return {
    type: 'stroke_play',
    format: 'nassau',
    holeResults,
    nassau,
    teamTotals,
    standings: nassau.points.map((pts, i) => ({ teamIndex: i, teamName: teams[i]?.name, points: pts, aggregate: teamTotals[i] })),
  }
}

// ============================================
// Hero (1v2 & 2v1) — match play per nine
// ============================================
function scoreHero(teams, scores, getNet, numHoles, gameConfig = {}) {
  // Hero: each team of 3, front 9 one player solo, back 9 another
  // Rotation config: gameConfig.heroRotation = { front: [soloPlayerIdA, soloPlayerIdB], back: [soloPlayerIdA, soloPlayerIdB] }
  // If not provided, default to first player front, second player back
  const holeResults = []

  const rotation = gameConfig.heroRotation || gameConfig.batmanRotation || {}

  const getMatchups = (hole) => {
    const isBack = hole > 9
    const nineKey = isBack ? 'back' : 'front'

    return teams.map((team, teamIdx) => {
      const otherTeam = teams[1 - teamIdx]
      // Use rotation config if available, otherwise default to index 0/1
      const configSolo = rotation[nineKey]?.[teamIdx]
      let solo, pair
      if (configSolo && team.playerIds.includes(configSolo)) {
        solo = configSolo
        pair = otherTeam.playerIds.filter((pid) => pid !== (rotation[nineKey]?.[1 - teamIdx]))
      } else {
        // Fallback: first player solo for front, second for back
        const soloIdx = isBack ? Math.min(1, team.playerIds.length - 1) : 0
        solo = team.playerIds[soloIdx]
        pair = otherTeam.playerIds.filter((_, i) => i !== soloIdx)
      }
      return { teamIdx, solo, pair }
    })
  }

  for (let hole = 1; hole <= numHoles; hole++) {
    const allScored = holeHasAllScores(teams, scores, hole)
    const matchups = getMatchups(hole)
    const matchResults = matchups.map(({ teamIdx, solo, pair }) => {
      const soloNet = getNet(solo, hole)
      const pairNets = pair.map((pid) => getNet(pid, hole)).filter((n) => n != null)
      const pairBest = pairNets.length > 0 ? Math.min(...pairNets) : null

      if (!allScored || soloNet == null || pairBest == null) {
        return { teamIdx, winner: null, pending: true, soloNet, pairBest }
      }

      let winner = null
      if (soloNet < pairBest) winner = teamIdx // solo's team wins
      else if (pairBest < soloNet) winner = 1 - teamIdx // other team wins
      // tie = pushed

      return { teamIdx, winner, pending: false, soloNet, pairBest }
    })

    holeResults.push({ hole, matchResults, pending: matchResults.some((m) => m.pending) })
  }

  // Per-nine scoring: count holes won per nine for each sub-match
  const nineResults = [
    { label: 'Front 9', start: 1, end: 9 },
    { label: 'Back 9', start: 10, end: 18 },
  ].map(({ label, start, end }) => {
    const teamWins = [0, 0]
    let played = 0
    for (const hr of holeResults) {
      if (hr.hole >= start && hr.hole <= end && !hr.pending) {
        played++
        for (const mr of hr.matchResults) {
          if (mr.winner != null) teamWins[mr.winner]++
        }
      }
    }
    let winner = null
    if (teamWins[0] > teamWins[1]) winner = 0
    else if (teamWins[1] > teamWins[0]) winner = 1

    // Dormie detection
    const totalHoles = 9
    const remaining = totalHoles - played
    let dormie = null
    if (remaining > 0 && winner == null) {
      const diff = teamWins[0] - teamWins[1]
      if (diff !== 0) {
        const losingTeam = diff > 0 ? 1 : 0
        const deficit = Math.abs(diff)
        if (deficit >= remaining) {
          dormie = { losingTeam, deficit, remaining, isDormie: true }
        }
      }
    }

    return { label, teamWins, winner, dormie }
  })

  const totalPoints = [0, 0]
  nineResults.forEach((nr) => {
    if (nr.winner != null) totalPoints[nr.winner]++
  })

  return {
    type: 'match_play',
    format: 'per_nine',
    holeResults,
    nineResults,
    standings: totalPoints.map((pts, i) => ({ teamIndex: i, teamName: teams[i]?.name, points: pts })),
  }
}

// ============================================
// High-Low (2v2) — low ball / high ball match play
// ============================================
function scoreHighLow(teams, scores, getNet, numHoles) {
  const holeResults = []

  for (let hole = 1; hole <= numHoles; hole++) {
    if (!holeHasAllScores(teams, scores, hole)) {
      holeResults.push({ hole, winner: null, teamScores: [], teamPoints: null, pending: true })
      continue
    }

    const teamScores = teams.map((team, idx) => {
      const nets = team.playerIds
        .map((pid) => getNet(pid, hole))
        .filter((n) => n != null)
        .sort((a, b) => a - b)
      return { teamIndex: idx, lowBall: nets[0] ?? null, highBall: nets[nets.length - 1] ?? null, nets }
    })

    // Compare low balls
    let lowWinner = null
    if (teamScores[0].lowBall != null && teamScores[1].lowBall != null) {
      if (teamScores[0].lowBall < teamScores[1].lowBall) lowWinner = 0
      else if (teamScores[1].lowBall < teamScores[0].lowBall) lowWinner = 1
    }

    // Compare high balls
    let highWinner = null
    if (teamScores[0].highBall != null && teamScores[1].highBall != null) {
      if (teamScores[0].highBall < teamScores[1].highBall) highWinner = 0
      else if (teamScores[1].highBall < teamScores[0].highBall) highWinner = 1
    }

    // Build team points: 0-2 per team per hole
    const teamPoints = [0, 0]
    if (lowWinner != null) teamPoints[lowWinner]++
    if (highWinner != null) teamPoints[highWinner]++

    // Overall hole winner: team with more points (null if split or full push)
    let winner = null
    if (teamPoints[0] > teamPoints[1]) winner = 0
    else if (teamPoints[1] > teamPoints[0]) winner = 1

    holeResults.push({ hole, winner, lowWinner, highWinner, teamPoints, teamScores, pending: false })
  }

  const nassau = computeNassau(holeResults, teams.length, { pointsPerHole: 2 })

  return {
    type: 'match_play',
    format: 'nassau',
    holeResults,
    nassau,
    standings: nassau.points.map((pts, i) => ({ teamIndex: i, teamName: teams[i]?.name, points: pts })),
  }
}

// ============================================
// 4-Ball (2v2) — best ball match play
// ============================================
function scoreFourBall(teams, scores, getNet, numHoles) {
  const holeResults = []

  for (let hole = 1; hole <= numHoles; hole++) {
    if (!holeHasAllScores(teams, scores, hole)) {
      holeResults.push({ hole, winner: null, teamScores: [], pending: true })
      continue
    }

    const teamScores = teams.map((team, idx) => {
      const nets = team.playerIds
        .map((pid) => getNet(pid, hole))
        .filter((n) => n != null)
        .sort((a, b) => a - b)
      return { teamIndex: idx, bestBall: nets[0] ?? null, nets }
    })

    const validScores = teamScores.filter((t) => t.bestBall != null)
    const minBest = Math.min(...validScores.map((t) => t.bestBall))
    const winners = validScores.filter((t) => t.bestBall === minBest)
    const winner = winners.length === 1 ? winners[0].teamIndex : null // tie = pushed

    holeResults.push({ hole, winner, teamScores, pending: false })
  }

  const nassau = computeNassau(holeResults, teams.length)

  return {
    type: 'match_play',
    format: 'nassau',
    holeResults,
    nassau,
    standings: nassau.points.map((pts, i) => ({ teamIndex: i, teamName: teams[i]?.name, points: pts })),
  }
}

// ============================================
// Next Ball (2v2) — like 4-Ball with tiebreaker
// ============================================
function scoreNextBall(teams, scores, getNet, numHoles) {
  const holeResults = []

  for (let hole = 1; hole <= numHoles; hole++) {
    if (!holeHasAllScores(teams, scores, hole)) {
      holeResults.push({ hole, winner: null, teamScores: [], pending: true })
      continue
    }

    const teamScores = teams.map((team, idx) => {
      const nets = team.playerIds
        .map((pid) => getNet(pid, hole))
        .filter((n) => n != null)
        .sort((a, b) => a - b)
      return { teamIndex: idx, bestBall: nets[0] ?? null, secondBall: nets[1] ?? null, nets }
    })

    let winner = null
    const [t0, t1] = teamScores

    if (t0.bestBall != null && t1.bestBall != null) {
      if (t0.bestBall < t1.bestBall) winner = 0
      else if (t1.bestBall < t0.bestBall) winner = 1
      else {
        // Tied best balls — compare second-best (Next Ball tiebreaker)
        if (t0.secondBall != null && t1.secondBall != null) {
          if (t0.secondBall < t1.secondBall) winner = 0
          else if (t1.secondBall < t0.secondBall) winner = 1
          // else both tie → pushed (winner stays null)
        }
      }
    }

    holeResults.push({ hole, winner, teamScores, pending: false })
  }

  const nassau = computeNassau(holeResults, teams.length)

  return {
    type: 'match_play',
    format: 'nassau',
    holeResults,
    nassau,
    standings: nassau.points.map((pts, i) => ({ teamIndex: i, teamName: teams[i]?.name, points: pts })),
  }
}

// ============================================
// Stableford — modified Stableford points team stroke play
// ============================================

/** Convert net score to Stableford points */
export function stablefordPoints(netScore, par) {
  if (netScore == null || par == null) return null
  const diff = netScore - par
  if (diff >= 2) return 0    // double bogey or worse
  if (diff === 1) return 1   // bogey
  if (diff === 0) return 2   // par
  if (diff === -1) return 4  // birdie
  if (diff <= -2) return 8   // eagle or better
  return 0
}

function scoreStableford(teams, scores, getNet, numHoles, gameConfig = {}) {
  const holePars = gameConfig.holePars || []
  const holeResults = []

  // Detect 3v4: the 4-man team uses "Top 3 of 4" scoring
  const is3v4 = teams.length === 2 &&
    [3, 4].includes(teams[0].playerIds.length) &&
    [3, 4].includes(teams[1].playerIds.length) &&
    teams[0].playerIds.length !== teams[1].playerIds.length

  for (let hole = 1; hole <= numHoles; hole++) {
    const par = holePars[hole - 1] || null
    const teamScores = teams.map((team, idx) => {
      const playerPoints = team.playerIds.map((pid) => {
        const net = getNet(pid, hole)
        if (net == null || par == null) return null
        return stablefordPoints(net, par)
      })
      let validPoints = playerPoints.filter((p) => p != null)
      // 3v4: drop lowest score from the 4-man team
      if (is3v4 && team.playerIds.length === 4 && validPoints.length === 4) {
        validPoints = validPoints.sort((a, b) => a - b).slice(1) // drop lowest
      }
      const teamPoints = validPoints.length > 0 ? validPoints.reduce((s, p) => s + p, 0) : null
      return { teamIndex: idx, teamPoints, playerPoints }
    })

    const pending = !holeHasAllScores(teams, scores, hole)
    holeResults.push({ hole, teamScores, pending })
  }

  // Compute per-segment totals (front/back/overall)
  const segmentTotal = (startHole, endHole) => {
    return teams.map((_, idx) => {
      let total = 0
      for (const hr of holeResults) {
        if (hr.hole >= startHole && hr.hole <= endHole && !hr.pending) {
          const ts = hr.teamScores.find((t) => t.teamIndex === idx)
          if (ts?.teamPoints != null) total += ts.teamPoints
        }
      }
      return total
    })
  }

  const holesPlayed = (startHole, endHole) =>
    holeResults.filter((hr) => hr.hole >= startHole && hr.hole <= endHole && !hr.pending).length

  const segmentWinner = (totals) => {
    const max = Math.max(...totals)
    if (max === 0) return null
    const winners = totals.filter((t) => t === max)
    if (winners.length > 1) return null // tie
    return totals.indexOf(max)
  }

  const frontTotals = segmentTotal(1, 9)
  const backTotals = segmentTotal(10, 18)
  const overallTotals = segmentTotal(1, 18)

  const front = {
    totals: frontTotals,
    winner: segmentWinner(frontTotals),
    complete: holesPlayed(1, 9) === 9,
  }
  const back = {
    totals: backTotals,
    winner: segmentWinner(backTotals),
    complete: holesPlayed(10, 18) === 9,
  }
  const overall = {
    totals: overallTotals,
    winner: segmentWinner(overallTotals),
    complete: holesPlayed(1, 18) === 18,
  }

  const points = teams.map((_, i) => {
    let pts = 0
    if (front.winner === i) pts++
    if (back.winner === i) pts++
    if (overall.winner === i) pts++
    return pts
  })

  return {
    type: 'stroke_play',
    format: 'stableford',
    holeResults,
    stableford: { front, back, overall },
    standings: points.map((pts, i) => ({
      teamIndex: i,
      teamName: teams[i]?.name,
      points: pts,
      totalPoints: overallTotals[i],
    })),
  }
}

// ============================================
// Six Pack — team stroke play with take/drop
// ============================================
function scoreSixPack(teams, scores, getNet, numHoles) {
  const holeResults = []

  for (let hole = 1; hole <= numHoles; hole++) {
    const teamScores = teams.map((team, idx) => {
      const playerNets = team.playerIds.map((pid) => {
        const net = getNet(pid, hole)
        const scoreRow = scores.find((s) => s.player_id === pid && s.hole_number === hole)
        const taken = scoreRow?.score_taken
        return { playerId: pid, net, taken }
      })
      return { teamIndex: idx, playerNets }
    })

    const pending = teamScores.some((t) => t.playerNets.some((p) => p.net == null))
    holeResults.push({ hole, teamScores, pending })
  }

  // Per-nine segment scoring: sum taken scores
  const segmentTotal = (startHole, endHole) => {
    return teams.map((_, idx) => {
      let total = 0
      let takenCount = 0
      for (const hr of holeResults) {
        if (hr.hole >= startHole && hr.hole <= endHole && !hr.pending) {
          const ts = hr.teamScores.find((t) => t.teamIndex === idx)
          if (ts) {
            for (const p of ts.playerNets) {
              if (p.taken !== false && p.net != null) {
                total += p.net
                takenCount++
              }
            }
          }
        }
      }
      return { total, takenCount }
    })
  }

  const holesPlayed = (startHole, endHole) =>
    holeResults.filter((hr) => hr.hole >= startHole && hr.hole <= endHole && !hr.pending).length

  const segmentWinner = (segData) => {
    // Winner = lowest total among teams with 12 taken scores
    const validTeams = segData.map((d, i) => ({ ...d, index: i })).filter((d) => d.takenCount === 12)
    if (validTeams.length === 0) return null
    const min = Math.min(...validTeams.map((t) => t.total))
    const winners = validTeams.filter((t) => t.total === min)
    if (winners.length > 1) return null // tie
    return winners[0].index
  }

  const frontData = segmentTotal(1, 9)
  const backData = segmentTotal(10, 18)

  // Overall: sum front + back taken scores (24 total)
  const overallData = teams.map((_, i) => ({
    total: frontData[i].total + backData[i].total,
    takenCount: frontData[i].takenCount + backData[i].takenCount,
  }))

  const front = {
    data: frontData,
    winner: segmentWinner(frontData),
    complete: holesPlayed(1, 9) === 9,
  }
  const back = {
    data: backData,
    winner: segmentWinner(backData),
    complete: holesPlayed(10, 18) === 9,
  }
  const overall = {
    data: overallData,
    winner: null, // determined below when both nines are complete
    complete: front.complete && back.complete,
  }

  // Override overall winner: lowest sum of front + back totals where both nines have 12 taken
  if (front.complete && back.complete) {
    const validTeams = teams.map((_, i) => ({
      index: i,
      total: frontData[i].total + backData[i].total,
      valid: frontData[i].takenCount === 12 && backData[i].takenCount === 12,
    })).filter((t) => t.valid)

    if (validTeams.length > 0) {
      const min = Math.min(...validTeams.map((t) => t.total))
      const winners = validTeams.filter((t) => t.total === min)
      overall.winner = winners.length === 1 ? winners[0].index : null
    }
  }

  const points = teams.map((_, i) => {
    let pts = 0
    if (front.winner === i) pts++
    if (back.winner === i) pts++
    if (overall.winner === i) pts++
    return pts
  })

  return {
    type: 'stroke_play',
    format: 'six_pack',
    holeResults,
    six_pack: { front, back, overall },
    standings: points.map((pts, i) => ({
      teamIndex: i,
      teamName: teams[i]?.name,
      points: pts,
      frontTotal: frontData[i].total,
      backTotal: backData[i].total,
      overallTotal: overallData[i].total,
    })),
  }
}

// ============================================
// Basic stroke play (fallback / no game type)
// ============================================
function scoreStroke(teams, scores, getNet, numHoles) {
  // Simple individual stroke play totals
  const allPlayerIds = teams.flatMap((t) => t.playerIds)
  const standings = allPlayerIds.map((pid) => {
    let gross = 0
    let net = 0
    let holesPlayed = 0
    for (let hole = 1; hole <= numHoles; hole++) {
      const g = scores.find((s) => s.player_id === pid && s.hole_number === hole)?.strokes
      if (g != null) {
        gross += g
        net += getNet(pid, hole) ?? g
        holesPlayed++
      }
    }
    return { playerId: pid, gross, net, holesPlayed }
  }).sort((a, b) => a.net - b.net)

  return {
    type: 'stroke_play',
    format: 'individual',
    standings,
    holeResults: [],
  }
}

// ============================================
// Money calculation
// ============================================

/**
 * Calculate money results for a completed round, including presses.
 * @param {Object} roundResults - Output from computeRoundResults
 * @param {Array} teams - [{ name, playerIds }]
 * @param {number} wager - $ per player
 * @param {string} gameType - Game type ID
 * @param {Array} presses - [{ segment, startHole, wager }] optional presses
 * @returns {Array} [{ playerId, amount }] — positive = winnings, negative = losses
 */
export function computeMoneyResults(roundResults, teams, wager, gameType, presses = [], options = {}) {
  if (!wager || wager <= 0) return []
  const { liveMode = false, nassauWeights } = options

  const playerMoney = {}
  teams.forEach((t) => t.playerIds.forEach((pid) => { playerMoney[pid] = 0 }))

  // Helper: check if all holes in a range have been scored
  const isRangeComplete = (startHole, endHole) => {
    if (!roundResults.holeResults) return false
    for (let h = startHole; h <= endHole; h++) {
      const hr = roundResults.holeResults.find(r => r.hole === h)
      if (!hr || hr.pending) return false
    }
    return true
  }

  const applyNassauMoney = (nassau, w, weights) => {
    const segWeights = weights || [1, 1, 1]
    const segments = [nassau.front, nassau.back, nassau.overall]
    segments.forEach((seg, idx) => {
      if (seg.winner == null) return
      const segWager = w * segWeights[idx]
      const winTeam = teams[seg.winner]

      teams.forEach((team, i) => {
        if (i === seg.winner) return
        const totalFromTeam = segWager * team.playerIds.length
        const perWinner = totalFromTeam / winTeam.playerIds.length

        team.playerIds.forEach((pid) => { playerMoney[pid] -= segWager })
        winTeam.playerIds.forEach((pid) => { playerMoney[pid] += perWinner })
      })
    })
  }

  // Helper to apply press money
  const applyPressMoney = (press) => {
    const pressResults = computePressResults(roundResults, press, teams)
    if (!pressResults) return
    // In live mode, only count if all holes in press range are scored
    if (liveMode) {
      const segEnd = press.segment === 'front' ? 9 : press.segment === 'back' ? 18 : 18
      if (!isRangeComplete(press.startHole, segEnd)) return
    }
    const pressWager = press.wager || wager
    if (pressResults.winner != null) {
      const winTeam = teams[pressResults.winner]
      const loseTeam = teams[1 - pressResults.winner]
      const perLoser = pressWager
      const totalPool = perLoser * loseTeam.playerIds.length
      const perWinner = totalPool / winTeam.playerIds.length
      loseTeam.playerIds.forEach((pid) => { playerMoney[pid] -= perLoser })
      winTeam.playerIds.forEach((pid) => { playerMoney[pid] += perWinner })
    }
  }

  if (roundResults.format === 'nassau') {
    // Nassau: original games play out based on actual results (no concession)
    let effectiveNassau = { ...roundResults.nassau }

    // In live mode, only pay out segments that are complete
    if (liveMode) {
      for (const seg of ['front', 'back', 'overall']) {
        if (!effectiveNassau[seg]?.complete) {
          effectiveNassau[seg] = { ...effectiveNassau[seg], winner: null }
        }
      }
    }

    applyNassauMoney(effectiveNassau, wager, nassauWeights)

    // Apply presses (run alongside the original game)
    for (const press of presses) {
      applyPressMoney(press)
    }
  } else if (roundResults.format === 'stableford') {
    // Stableford: 3 nassau-like games (front, back, overall) — highest points wins
    const { stableford } = roundResults
    if (!stableford) return []
    const stablefordWeights = nassauWeights || [1, 1, 1]

    const segKeys = ['front', 'back', 'overall']
    for (let idx = 0; idx < segKeys.length; idx++) {
      const segData = stableford[segKeys[idx]]
      if (segData?.winner == null) continue
      if (liveMode && !segData.complete) continue

      const segWager = wager * stablefordWeights[idx]
      const winTeam = teams[segData.winner]
      teams.forEach((team, i) => {
        if (i === segData.winner) return
        const totalFromTeam = segWager * team.playerIds.length
        const perWinner = totalFromTeam / winTeam.playerIds.length

        team.playerIds.forEach((pid) => { playerMoney[pid] -= segWager })
        winTeam.playerIds.forEach((pid) => { playerMoney[pid] += perWinner })
      })
    }

    // Apply presses for stableford
    for (const press of presses) {
      applyPressMoney(press)
    }
  } else if (roundResults.format === 'six_pack') {
    // Six Pack: 3 nassau-like games (front, back, overall) — lowest taken total wins
    const { six_pack } = roundResults
    if (!six_pack) return []
    const sixPackWeights = nassauWeights || [1, 1, 1]

    const segKeys = ['front', 'back', 'overall']
    for (let idx = 0; idx < segKeys.length; idx++) {
      const segData = six_pack[segKeys[idx]]
      if (segData?.winner == null) continue
      if (liveMode && !segData.complete) continue

      const segWager = wager * sixPackWeights[idx]
      const winTeam = teams[segData.winner]
      teams.forEach((team, i) => {
        if (i === segData.winner) return
        const totalFromTeam = segWager * team.playerIds.length
        const perWinner = totalFromTeam / winTeam.playerIds.length

        team.playerIds.forEach((pid) => { playerMoney[pid] -= segWager })
        winTeam.playerIds.forEach((pid) => { playerMoney[pid] += perWinner })
      })
    }

    // Apply presses for Six Pack
    for (const press of presses) {
      applyPressMoney(press)
    }
  } else if (roundResults.format === 'per_nine') {
    const { nineResults } = roundResults
    if (!nineResults) return []

    nineResults.forEach((nr, idx) => {
      if (nr.winner == null) return

      // In live mode, only pay if all holes in the nine are scored
      if (liveMode) {
        const start = idx === 0 ? 1 : 10
        const end = idx === 0 ? 9 : 18
        if (!isRangeComplete(start, end)) return
      }

      const winTeam = teams[nr.winner]
      const loseTeam = teams[1 - nr.winner]
      const perLoser = wager
      const totalPool = perLoser * loseTeam.playerIds.length
      const perWinner = totalPool / winTeam.playerIds.length

      loseTeam.playerIds.forEach((pid) => { playerMoney[pid] -= perLoser })
      winTeam.playerIds.forEach((pid) => { playerMoney[pid] += perWinner })
    })

    // Apply presses for per_nine format
    for (const press of presses) {
      const pressResults = computePressResults(roundResults, press, teams)
      if (pressResults && pressResults.winner != null) {
        // In live mode, only count if all holes in press range are scored
        if (liveMode) {
          const segEnd = press.segment === 'front' ? 9 : press.segment === 'back' ? 18 : 18
          if (!isRangeComplete(press.startHole, segEnd)) continue
        }

        const pressWager = press.wager || wager
        const winTeam = teams[pressResults.winner]
        const loseTeam = teams[1 - pressResults.winner]
        const perLoser = pressWager
        const totalPool = perLoser * loseTeam.playerIds.length
        const perWinner = totalPool / winTeam.playerIds.length

        loseTeam.playerIds.forEach((pid) => { playerMoney[pid] -= perLoser })
        winTeam.playerIds.forEach((pid) => { playerMoney[pid] += perWinner })
      }
    }
  }

  return Object.entries(playerMoney).map(([playerId, amount]) => ({ playerId, amount }))
}

/**
 * Compute the result of a press from the starting hole to end of segment.
 * A press is essentially a new match starting from startHole.
 * @param {Object} roundResults - Full round results with holeResults
 * @param {Object} press - { segment, startHole }
 * @param {Array} teams - [{ name, playerIds }]
 * @returns {{ winner: number|null, wins: number[] }}
 */
export function computePressResults(roundResults, press, teams) {
  const { holeResults } = roundResults
  if (!holeResults) return null

  const segEnd = press.segment === 'front' ? 9
    : press.segment === 'back' ? 18
    : 18

  // Stroke play formats: compare cumulative totals in press range
  if (roundResults.format === 'stableford') {
    // Stableford: sum team points in press range — highest wins
    const totals = teams.map((_, idx) => {
      let total = 0
      for (const hr of holeResults) {
        if (hr.hole >= press.startHole && hr.hole <= segEnd && !hr.pending) {
          const ts = hr.teamScores?.find(t => t.teamIndex === idx)
          if (ts?.teamPoints != null) total += ts.teamPoints
        }
      }
      return total
    })
    const max = Math.max(...totals)
    if (max === 0) return { winner: null, wins: totals, totals }
    const winners = totals.filter(t => t === max)
    const winner = winners.length === 1 ? totals.indexOf(max) : null
    return { winner, wins: totals, totals }
  }

  if (roundResults.format === 'six_pack') {
    // Six Pack: sum taken scores in press range — lowest wins
    const totals = teams.map((_, idx) => {
      let total = 0
      for (const hr of holeResults) {
        if (hr.hole >= press.startHole && hr.hole <= segEnd && !hr.pending) {
          const ts = hr.teamScores?.find(t => t.teamIndex === idx)
          if (ts) {
            for (const p of ts.playerNets) {
              if (p.taken !== false && p.net != null) total += p.net
            }
          }
        }
      }
      return total
    })
    const nonZero = totals.filter(t => t > 0)
    if (nonZero.length === 0) return { winner: null, wins: totals, totals }
    const min = Math.min(...nonZero)
    const winners = totals.filter(t => t === min && t > 0)
    const winner = winners.length === 1 ? totals.indexOf(min) : null
    return { winner, wins: totals, totals }
  }

  if (roundResults.type === 'stroke_play' && roundResults.format === 'nassau') {
    // Duels: sum best ball scores in press range — lowest wins
    const totals = teams.map((_, idx) => {
      let total = 0
      for (const hr of holeResults) {
        if (hr.hole >= press.startHole && hr.hole <= segEnd && !hr.pending) {
          const ts = hr.teamScores?.find(t => t.teamIndex === idx)
          if (ts?.bestBall != null) total += ts.bestBall
        }
      }
      return total
    })
    const nonZero = totals.filter(t => t > 0)
    if (nonZero.length === 0) return { winner: null, wins: totals, totals }
    const min = Math.min(...nonZero)
    const winners = totals.filter(t => t === min && t > 0)
    const winner = winners.length === 1 ? totals.indexOf(min) : null
    return { winner, wins: totals, totals }
  }

  // Match play: count hole winners (or team points for High-Low) in press range
  const wins = [0, 0]
  for (const hr of holeResults) {
    if (hr.hole >= press.startHole && hr.hole <= segEnd && !hr.pending) {
      if (hr.teamPoints) {
        wins[0] += (hr.teamPoints[0] || 0)
        wins[1] += (hr.teamPoints[1] || 0)
      } else if (hr.winner != null) {
        wins[hr.winner]++
      }
      // For hero matchResults
      if (hr.matchResults) {
        for (const mr of hr.matchResults) {
          if (mr.winner != null) wins[mr.winner]++
        }
      }
    }
  }

  let winner = null
  if (wins[0] > wins[1]) winner = 0
  else if (wins[1] > wins[0]) winner = 1

  return { winner, wins }
}

/**
 * Compute match play notation for a hole range.
 * Returns { text, winner, clinchHole } where winner is 0 or 1 (team index) or null (tied).
 * text is e.g. "4 & 3" (clinched early), "2 UP" (leading/final), "AS" (all square).
 * clinchHole is the hole number where the match was clinched, or null if not clinched.
 * Returns null if no holes have been played in the range.
 */
export function computeSegmentMatchStatus(holeResults, startHole, endHole, segmentEnd) {
  if (!holeResults) return null
  const effectiveEnd = segmentEnd || endHole

  const rangeResults = holeResults.filter(
    (hr) => hr.hole >= startHole && hr.hole <= endHole && !hr.pending
  )
  if (rangeResults.length === 0) return null

  // Auto-detect multi-point (High-Low) from hole data
  const hasTeamPoints = rangeResults.some(hr => hr.teamPoints)
  const ppHole = hasTeamPoints ? 2 : 1

  const wins = [0, 0]

  // Walk hole by hole to detect clinch
  for (let h = startHole; h <= endHole; h++) {
    const hr = holeResults.find((r) => r.hole === h && !r.pending)
    if (!hr) continue
    if (hr.teamPoints) {
      wins[0] += (hr.teamPoints[0] || 0)
      wins[1] += (hr.teamPoints[1] || 0)
    } else if (hr.winner != null) {
      wins[hr.winner]++
    }

    const diff = Math.abs(wins[0] - wins[1])
    const remaining = effectiveEnd - h
    if (diff > 0 && diff > remaining * ppHole && h < effectiveEnd) {
      // Clinched before the last hole
      const winner = wins[0] > wins[1] ? 0 : 1
      return { text: `${diff} & ${remaining}`, winner, clinchHole: h }
    }
  }

  // Not clinched — final or in-progress
  const diff = wins[0] - wins[1]
  if (diff === 0) {
    return { text: 'AS', winner: null, clinchHole: null }
  }
  const winner = diff > 0 ? 0 : 1
  return { text: `${Math.abs(diff)} UP`, winner, clinchHole: null }
}

/** Return press label like Press F1, Press B2, Press T1 */
export function getPressLabel(segment, number) {
  const prefix = segment === 'front' ? 'F' : segment === 'back' ? 'B' : 'T'
  return `Press ${prefix}${number}`
}

/**
 * Extract team leaderboard points from round results.
 * Half points (0.5) awarded to each team for tied completed segments.
 * Returns number[] (points per team index), or null for unsupported game types.
 */
export function extractTeamPoints(results, numTeams) {
  if (!results) return null

  if (results.nassau?.points) {
    return results.nassau.points
  }

  const segmentPoints = (data) => {
    if (!data) return null
    return Array.from({ length: numTeams }, (_, i) => {
      let pts = 0
      for (const seg of ['front', 'back', 'overall']) {
        if (data[seg]?.winner === i) pts++
        else if (data[seg]?.winner === null && data[seg]?.complete) pts += 0.5
      }
      return pts
    })
  }

  if (results.stableford) return segmentPoints(results.stableford)
  if (results.six_pack) return segmentPoints(results.six_pack)

  if (results.nineResults) {
    return Array.from({ length: numTeams }, (_, i) => {
      let pts = 0
      for (const nr of results.nineResults) {
        if (nr.winner === i) pts++
        else if (nr.winner === null && nr.complete) pts += 0.5
      }
      return pts
    })
  }

  return null
}
